import { useEffect, useRef, useCallback } from "react";
import { supabase } from "../supabaseClient";

/**
 * Hook that monitors user activity and automatically refreshes the Supabase session
 * to prevent session expiration during active use (e.g., filling out forms).
 *
 * @param {Object} options
 * @param {number} options.activityCheckInterval - How often to check for activity (ms). Default: 5 minutes.
 * @param {number} options.refreshInterval - Minimum time between session refreshes (ms). Default: 10 minutes.
 * @param {boolean} options.enabled - Whether session extending is enabled. Default: true.
 */
export function useSessionExtender({
  activityCheckInterval = 5 * 60 * 1000, // 5 minutes
  refreshInterval = 10 * 60 * 1000, // 10 minutes
  enabled = true,
} = {}) {
  const lastActivityRef = useRef(Date.now());
  const lastRefreshRef = useRef(Date.now());
  const isActiveRef = useRef(false);

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    isActiveRef.current = true;
  }, []);

  // Refresh session if user has been active
  const checkAndRefreshSession = useCallback(async () => {
    if (!enabled) return;

    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshRef.current;
    const timeSinceLastActivity = now - lastActivityRef.current;

    // Only refresh if:
    // 1. User was active since last check
    // 2. Enough time has passed since last refresh
    // 3. Activity was recent (within the check interval)
    if (
      isActiveRef.current &&
      timeSinceLastRefresh >= refreshInterval &&
      timeSinceLastActivity < activityCheckInterval
    ) {
      console.log("[useSessionExtender] User active, refreshing session...");
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error) {
          console.error("[useSessionExtender] Session refresh failed:", error);
        } else if (data.session) {
          console.log("[useSessionExtender] Session refreshed successfully");
          lastRefreshRef.current = Date.now();
        }
      } catch (err) {
        console.error("[useSessionExtender] Error refreshing session:", err);
      }
    }

    // Reset activity flag for next interval
    isActiveRef.current = false;
  }, [enabled, activityCheckInterval, refreshInterval]);

  useEffect(() => {
    if (!enabled) return;

    // Activity events to monitor
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
      "input",
      "change",
    ];

    // Debounced activity handler to avoid excessive updates
    let activityTimeout = null;
    const handleActivity = () => {
      if (activityTimeout) return;
      activityTimeout = setTimeout(() => {
        updateActivity();
        activityTimeout = null;
      }, 1000); // Debounce to 1 second
    };

    // Add event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Set up periodic session check
    const intervalId = setInterval(checkAndRefreshSession, activityCheckInterval);

    // Initial activity mark
    updateActivity();

    console.log("[useSessionExtender] Session extender initialized");

    return () => {
      // Cleanup
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      clearInterval(intervalId);
      if (activityTimeout) clearTimeout(activityTimeout);
      console.log("[useSessionExtender] Session extender cleaned up");
    };
  }, [enabled, activityCheckInterval, checkAndRefreshSession, updateActivity]);

  return {
    refreshSession: checkAndRefreshSession,
    markActive: updateActivity,
  };
}
